/**
 * Canonical hook for organisation-level role checks.
 *
 * Uses the backend-authoritative useOrganizations() data and normalises
 * any role format ("Admin", "admin", "org:admin") to a lowercase base
 * string so that all consumers get consistent results.
 */

import { useOrganizations } from '@/hooks/use-organizations';

export type OrgRoleName = 'admin' | 'manager' | 'member' | 'viewer';

const KNOWN_ROLES = new Set<string>(['admin', 'manager', 'member', 'viewer']);

function normalizeRole(raw: string): OrgRoleName {
  let base = raw.trim().toLowerCase();
  const colonIdx = base.lastIndexOf(':');
  if (colonIdx !== -1) {
    base = base.slice(colonIdx + 1);
  }
  if (KNOWN_ROLES.has(base)) return base as OrgRoleName;
  return 'member';
}

export interface UseOrgRoleReturn {
  role: OrgRoleName | null;
  /** Raw role name from the backend (e.g. "Admin"). */
  roleName: string | null;
  isAdmin: boolean;
  isAdminOrManager: boolean;
  isLoading: boolean;
}

export function useOrgRole(): UseOrgRoleReturn {
  const { organizations, isLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const rawName = activeOrg?.role.name ?? null;
  const role = rawName ? normalizeRole(rawName) : null;

  return {
    role,
    roleName: rawName,
    isAdmin: role === 'admin',
    isAdminOrManager: role === 'admin' || role === 'manager',
    isLoading,
  };
}
